import React, { ReactNode, useMemo } from "react";
import classNames from "classnames";
import { Tooltip as ReactTooltip, PlacesType } from "react-tooltip";
import styled from "styled-components";

const TooltipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
`;

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 512 512"
    fill="currentColor"
  >
    <path d="M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm17.2 109.6c3.3 3.6 5 8.1 5 13.4 0 5.3-1.7 9.8-5 13.4-3.3 3.6-7.5 5.4-12.6 5.4-5.1 0-9.3-1.8-12.6-5.4-3.3-3.6-5-8.1-5-13.4 0-5.3 1.7-9.8 5-13.4 3.3-3.6 7.5-5.4 12.6-5.4 5.1 0 9.3 1.8 12.6 5.4zM298.3 360H217v-21.6h25.2V237.7H220v-21.6h53.1v122.4h25.2V360z" />
  </svg>
);

const InfoIconOutline = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 512 512"
    fill="currentColor"
  >
    <path d="M256 90c44.3 0 86 17.3 117.4 48.6C404.7 170 422 211.7 422 256s-17.3 86-48.6 117.4C342 404.7 300.3 422 256 422s-86-17.3-117.4-48.6C107.3 342 90 300.3 90 256s17.3-86 48.6-117.4C170 107.3 211.7 90 256 90m0-42C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48z" />
    <path d="M277 360h-42V235h42v125zm0-166h-42v-42h42v42z" />
  </svg>
);

interface ITooltip {
  className?: string;
  classNameWrapper?: string;
  children: ReactNode;
  content: ReactNode;
  displayIcon?: boolean;
  interactive?: boolean;
  variant?: "default" | "outline";
  large?: boolean;
  place?: PlacesType;
  backgroundColor?: string;
}

/**
 * Tooltip component that displays additional information when hovering over children
 *
 * @param props - Component props
 * @param props.className - Additional CSS class to apply to the tooltip content
 * @param props.classNameWrapper - Additional CSS class to apply to the tooltip wrapper
 * @param props.children - Content to wrap with the tooltip functionality
 * @param props.content - Content to display inside the tooltip
 * @param props.displayIcon - Whether to display the information icon next to children. Default is true
 * @param props.interactive - Whether the tooltip content is interactive (clickable). Default is false
 * @param props.variant - Icon variant: 'default' or 'outline'. Default is 'default'
 * @param props.place - Position of the tooltip relative to the children. Default is 'right'
 * @param props.backgroundColor - Background color of the tooltip. Default is 'var(--primary-190, #1a1a1a)'
 *
 * @returns Tooltip component
 */
function Tooltip({
  className,
  classNameWrapper,
  children,
  content,
  displayIcon = true,
  interactive = false,
  variant = "default",
  large = false,
  place = "right",
  backgroundColor = "var(--primary-190, #1a1a1a)",
}: ITooltip) {
  const tooltipId = useMemo(
    () => `tooltip-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  const tooltipIcon = useMemo(() => {
    return variant === "outline" ? <InfoIconOutline /> : <InfoIcon />;
  }, [variant]);

  return (
    <>
      <TooltipWrapper
        data-tooltip-id={tooltipId}
        className={classNames(classNameWrapper)}
      >
        <div className="d-flex gap-8 align-items-center">
          {children}
          {displayIcon && (
            <div style={{ marginLeft: "8px" }}>{tooltipIcon}</div>
          )}
        </div>
      </TooltipWrapper>
      <ReactTooltip
        id={tooltipId}
        place={place}
        className={classNames(className)}
        clickable={interactive}
        delayShow={50} // ms
        delayHide={interactive ? 200 : 50}
        noArrow={false}
        opacity={1}
        style={{
          backgroundColor,
          fontFamily: "var(--font-alternative)",
          color: "var(--neutral-light, #ffffff)",
          fontSize: "12px",
          lineHeight: "16px",
          padding: "8px 12px",
          maxWidth: large ? "500px" : "250px",
          borderRadius: "4px",
          zIndex: 1100,
          whiteSpace: "normal",
          pointerEvents: interactive ? "auto" : "none",
        }}
      >
        {content}
      </ReactTooltip>
    </>
  );
}

export default Tooltip;
